import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getPublishedQuestions } from '../services/versionService';
import { submitScan } from '../services/scanService';
import PillarSection from '../components/trilogy/PillarSection';
import ProgressIndicator from '../components/trilogy/ProgressIndicator';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import { useNavigate } from 'react-router-dom';
import './TrilogyAssessment.css';

const TrilogyAssessment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState('en');
  const [questionData, setQuestionData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [currentPillar, setCurrentPillar] = useState(0);

  const pillars = ['LEAD', 'EVOLVE', 'ALIGN', 'PERFORM'];

  useEffect(() => {
    loadQuestions();
    // Load saved language preference
    const savedLang = localStorage.getItem('trilogy_language');
    if (savedLang) {
      setLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    // Save language preference
    localStorage.setItem('trilogy_language', language);
  }, [language]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await getPublishedQuestions();
      setQuestionData(data);

      // Initialize answers object
      const initialAnswers = {};
      data.questions.forEach(q => {
        if (q.type === 'scored') {
          initialAnswers[q.key] = null;
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Error loading questions:', error);
      alert('Failed to load assessment questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));

    // Clear error for this question
    if (errors[questionKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionKey];
        return newErrors;
      });
    }
  };

  const validateAnswers = () => {
    const newErrors = {};
    let isValid = true;

    questionData.questions.forEach(q => {
      if (q.type === 'scored' && (answers[q.key] === null || answers[q.key] === undefined)) {
        newErrors[q.key] = 'This question is required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateAnswers()) {
      alert('Please answer all questions before submitting.');
      return;
    }

    try {
      setSubmitting(true);

      const scanData = {
        answers,
        questionsVersion: questionData.version,
        language,
        metadata: {
          device: 'web',
          startTime: Date.now(),
          userAgent: navigator.userAgent
        }
      };

      const result = await submitScan(scanData);

      // Navigate to results page
      navigate(`/trilogy/results/${result.id}`);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getQuestionsByPillar = (pillar) => {
    if (!questionData) return [];
    return questionData.questions
      .filter(q => q.pillar === pillar && q.type === 'scored')
      .sort((a, b) => a.orderIndex - b.orderIndex);
  };

  const getBonusQuestions = () => {
    if (!questionData) return [];
    return questionData.questions
      .filter(q => q.type === 'bonus')
      .sort((a, b) => a.orderIndex - b.orderIndex);
  };

  const getTotalAnswered = () => {
    return Object.values(answers).filter(a => a !== null && a !== undefined).length;
  };

  const getTotalQuestions = () => {
    if (!questionData) return 0;
    return questionData.questions.filter(q => q.type === 'scored').length;
  };

  const getCompletionPercentage = () => {
    const total = getTotalQuestions();
    if (total === 0) return 0;
    return Math.round((getTotalAnswered() / total) * 100);
  };

  if (loading) {
    return (
      <div className="trilogy-assessment-container">
        <Loader />
      </div>
    );
  }

  if (!questionData || questionData.questions.length === 0) {
    return (
      <div className="trilogy-assessment-container">
        <div className="trilogy-empty-state">
          <h2>No Assessment Available</h2>
          <p>The assessment is not currently available. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trilogy-assessment-container">
      {/* Header */}
      <motion.div
        className="trilogy-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="trilogy-header-content">
          <h1>Trilogy Assessment</h1>
          <p>Evaluate your organization across four key pillars</p>
        </div>

        {/* Language Selector */}
        <div className="trilogy-language-selector">
          <button
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            English
          </button>
          <button
            className={`lang-btn ${language === 'mr' ? 'active' : ''}`}
            onClick={() => setLanguage('mr')}
          >
            मराठी
          </button>
        </div>
      </motion.div>

      {/* Progress Indicator */}
      <ProgressIndicator
        currentPillar={pillars[currentPillar]}
        totalQuestions={getTotalQuestions()}
        answeredQuestions={getTotalAnswered()}
        completionPercentage={getCompletionPercentage()}
      />

      {/* Assessment Content */}
      <div className="trilogy-content">
        {/* Pillar Sections */}
        {pillars.map((pillar, index) => (
          <PillarSection
            key={pillar}
            pillar={pillar}
            questions={getQuestionsByPillar(pillar)}
            answers={answers}
            errors={errors}
            language={language}
            onAnswerChange={handleAnswerChange}
            onFocus={() => setCurrentPillar(index)}
          />
        ))}

        {/* Bonus Questions */}
        {getBonusQuestions().length > 0 && (
          <PillarSection
            pillar="BONUS"
            questions={getBonusQuestions()}
            answers={answers}
            errors={errors}
            language={language}
            onAnswerChange={handleAnswerChange}
            isBonus={true}
          />
        )}

        {/* Submit Button */}
        <motion.div
          className="trilogy-submit-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="trilogy-submit-info">
            <p>
              {getTotalAnswered()} of {getTotalQuestions()} questions answered
            </p>
            {getTotalAnswered() < getTotalQuestions() && (
              <p className="trilogy-warning">
                Please answer all questions before submitting
              </p>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting || getTotalAnswered() < getTotalQuestions()}
            style={{ minWidth: '200px' }}
          >
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </Button>
        </motion.div>
      </div>

      {/* Version Info */}
      <div className="trilogy-footer">
        <p>Assessment Version: {questionData.version}</p>
        {questionData.isDraft && (
          <p className="trilogy-draft-notice"></p>
        )}
      </div>
    </div>
  );
};

export default TrilogyAssessment;
